import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    document.title = 'Privacy Policy | Miami Comfort Group';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Privacy Policy for Miami Comfort Group. Learn how we collect, use, and protect your personal information.');
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://miamicomfort.group';
    
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/privacy-policy`);

    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Quicksand']">
      <Navbar />

      <section className="bg-black py-20 md:py-28">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <h1 className="font-['Oxygen'] font-bold text-[40px] md:text-[56px] lg:text-[64px] text-white mb-6 leading-[1.1]">
            Privacy Policy
          </h1>
          <p className="text-white/70 text-[16px] md:text-[18px]">
            Effective Date: January 1, 2025
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 space-y-12 md:space-y-16">
          
          {/* Introduction */}
          <div>
            <h2 className="font-['Oxygen'] font-bold text-[28px] md:text-[36px] text-black mb-6">
              Introduction
            </h2>
            <div className="space-y-4 text-[#333333] text-[15px] md:text-[16px] leading-[1.8]">
              <p>
                Miami Comfort Group LLC (<strong>"MCG"</strong>, <strong>"we"</strong>, <strong>"us"</strong>, or <strong>"our"</strong>) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <a href="https://miamicomfort.group" className="text-[#A7D0CC] hover:underline cursor-pointer">miamicomfort.group</a> and use our services.
              </p>
              <p>
                Real estate services are provided by Elisabet Sierra Bermejo, a licensed real estate sales associate with Avanti Way Realty, LLC (License #SL3535387).
              </p>
              <p>
                By using our website, you consent to the data practices described in this policy. If you do not agree with this policy, please do not use our website.
              </p>
            </div>
          </div>

          {/* Google Analytics */}
          <div>
            <h2 className="font-['Oxygen'] font-bold text-[28px] md:text-[36px] text-black mb-6">
              Use of Google Analytics and Tracking Cookies
            </h2>
            <div className="space-y-4 text-[#333333] text-[15px] md:text-[16px] leading-[1.8]">
              <p>
                We use <strong>Google Analytics</strong>, a web analytics service provided by Google LLC, to help us understand how visitors interact with our website. Google Analytics uses cookies and similar tracking technologies to collect information about your use of our site.
              </p>
              
              <h3 className="font-['Oxygen'] font-bold text-[20px] md:text-[24px] text-black mt-6 mb-3">
                Data Collected by Google Analytics
              </h3>
              <p>Google Analytics may collect the following types of information:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Pages you visit on our website</li>
                <li>Time spent on each page</li>
                <li>How you arrived at our site (referral source)</li>
                <li>Device type, browser type, and operating system</li>
                <li>Approximate geographic location (city/region level)</li>
                <li>IP address (anonymized)</li>
              </ul>

              <h3 className="font-['Oxygen'] font-bold text-[20px] md:text-[24px] text-black mt-6 mb-3">
                Purpose and Legal Basis
              </h3>
              <p>
                We use this data to analyze website traffic, improve user experience, and optimize our content and services. Our legal basis for processing this data is our legitimate interest in understanding and improving our website performance.
              </p>

              <h3 className="font-['Oxygen'] font-bold text-[20px] md:text-[24px] text-black mt-6 mb-3">
                Third-Party Access
              </h3>
              <p>
                Google Analytics data is processed by Google in accordance with Google's Privacy Policy. Google may use this data in aggregate form for its own purposes. For more information, visit <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#A7D0CC] hover:underline cursor-pointer">Google's Privacy Policy</a>.
              </p>
            </div>
          </div>

          {/* Contact Forms */}
          <div>
            <h2 className="font-['Oxygen'] font-bold text-[28px] md:text-[36px] text-black mb-6">
              Data Collected Through Contact Forms
            </h2>
            <div className="space-y-4 text-[#333333] text-[15px] md:text-[16px] leading-[1.8]">
              <p>
                When you submit a contact form, consultation request, or newsletter subscription on our website, we collect the following personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Name:</strong> To address you personally in our communications</li>
                <li><strong>Email address:</strong> To respond to your inquiries and send requested information</li>
                <li><strong>Phone number:</strong> To contact you regarding your real estate needs (when provided)</li>
                <li><strong>Message content:</strong> To understand your specific questions, needs, or investment goals</li>
                <li><strong>Investment interest:</strong> To provide relevant information about our services</li>
              </ul>

              <h3 className="font-['Oxygen'] font-bold text-[20px] md:text-[24px] text-black mt-6 mb-3">
                How We Use Your Information
              </h3>
              <p>We use the information you provide to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Respond to your inquiries and consultation requests</li>
                <li>Provide information about our real estate consulting services</li>
                <li>Schedule appointments and consultations</li>
                <li>Send newsletters and updates (only if you have subscribed)</li>
                <li>Improve our services and customer experience</li>
              </ul>

              <h3 className="font-['Oxygen'] font-bold text-[20px] md:text-[24px] text-black mt-6 mb-3">
                Data Storage and Security
              </h3>
              <p>
                Form submissions are processed through secure third-party services (Readdy.ai). We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
              </p>
              <p>
                We retain your information only as long as necessary to fulfill the purposes outlined in this policy or as required by law.
              </p>
            </div>
          </div>

          {/* No Sale of Data */}
          <div>
            <h2 className="font-['Oxygen'] font-bold text-[28px] md:text-[36px] text-black mb-6">
              We Do Not Sell Your Personal Data
            </h2>
            <div className="space-y-4 text-[#333333] text-[15px] md:text-[16px] leading-[1.8]">
              <p className="font-medium">
                <strong>Miami Comfort Group does not sell, rent, or share your personal information with third parties for commercial purposes.</strong>
              </p>
              <p>
                We may share your information only in the following limited circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Service Providers:</strong> With trusted third-party service providers who assist us in operating our website and conducting our business (e.g., email service providers, form processors), under strict confidentiality agreements</li>
                <li><strong>Legal Compliance:</strong> When required by law, court order, or government regulation</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, where your information may be transferred to the new owner</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
              </ul>
              <p>
                We do not engage in data brokering, and we do not allow third parties to collect personal information from our website for their own marketing purposes.
              </p>
            </div>
          </div>

          {/* Opt-Out Rights */}
          <div>
            <h2 className="font-['Oxygen'] font-bold text-[28px] md:text-[36px] text-black mb-6">
              Your Right to Opt-Out
            </h2>
            <div className="space-y-4 text-[#333333] text-[15px] md:text-[16px] leading-[1.8]">
              <p>
                You have the right to control how your information is collected and used. Here's how you can exercise your opt-out rights:
              </p>

              <h3 className="font-['Oxygen'] font-bold text-[20px] md:text-[24px] text-black mt-6 mb-3">
                Opt-Out of Google Analytics Tracking
              </h3>
              <p>You can prevent Google Analytics from tracking your activity by:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Browser Settings:</strong> Most web browsers allow you to block or delete cookies through your browser settings. Consult your browser's help documentation for instructions.
                </li>
                <li>
                  <strong>Google Analytics Opt-Out Browser Add-On:</strong> Install the official <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[#A7D0CC] hover:underline cursor-pointer">Google Analytics Opt-Out Browser Add-on</a>, which prevents your data from being used by Google Analytics across all websites.
                </li>
              </ul>

              <h3 className="font-['Oxygen'] font-bold text-[20px] md:text-[24px] text-black mt-6 mb-3">
                Opt-Out of Marketing Communications
              </h3>
              <p>
                If you have subscribed to our newsletter or marketing emails, you can unsubscribe at any time by clicking the "unsubscribe" link at the bottom of any email we send you.
              </p>

              <h3 className="font-['Oxygen'] font-bold text-[20px] md:text-[24px] text-black mt-6 mb-3">
                Request Deletion of Your Personal Data
              </h3>
              <p>
                You have the right to request that we delete any personal information we have collected from you through contact forms or other means. To exercise this right, please contact us at <a href="mailto:hello@miamicomfort.group" className="text-[#A7D0CC] hover:underline cursor-pointer">hello@miamicomfort.group</a> with the subject line "Data Deletion Request."
              </p>
              <p>
                We will respond to your request within 30 days and delete your information unless we are required to retain it for legal or legitimate business purposes.
              </p>
            </div>
          </div>

          {/* Contact for Privacy Questions */}
          <div>
            <h2 className="font-['Oxygen'] font-bold text-[28px] md:text-[36px] text-black mb-6">
              Contact Us About Privacy
            </h2>
            <div className="space-y-4 text-[#333333] text-[15px] md:text-[16px] leading-[1.8]">
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your personal information, please contact us:
              </p>
              <div className="bg-[#E8E2DB] rounded-xl p-6 md:p-8 space-y-3">
                <p className="font-medium text-black">Miami Comfort Group LLC</p>
                <p>
                  <strong>Email:</strong> <a href="mailto:hello@miamicomfort.group" className="text-[#A7D0CC] hover:underline cursor-pointer">hello@miamicomfort.group</a>
                </p>
                <p>
                  <strong>Phone:</strong> <a href="tel:+17868568145" className="text-[#A7D0CC] hover:underline cursor-pointer">+1 786 856 8145</a>
                </p>
                <p>
                  <strong>Address:</strong> Miami, FL, USA
                </p>
              </div>
            </div>
          </div>

          {/* Changes to Policy */}
          <div>
            <h2 className="font-['Oxygen'] font-bold text-[28px] md:text-[36px] text-black mb-6">
              Changes to This Privacy Policy
            </h2>
            <div className="space-y-4 text-[#333333] text-[15px] md:text-[16px] leading-[1.8]">
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. When we make changes, we will update the "Effective Date" at the top of this page.
              </p>
              <p>
                We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.
              </p>
            </div>
          </div>

          {/* Back to Home */}
          <div className="pt-8 border-t border-[#E8E2DB]">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-[#A7D0CC] hover:text-[#8CBEB9] transition-colors cursor-pointer text-[15px] md:text-[16px] font-medium"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Back to Home</span>
            </Link>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black relative overflow-hidden">
        <div className="absolute bottom-0 left-0 right-0 font-['Oxygen'] font-bold text-[100px] md:text-[140px] lg:text-[180px] text-white/[0.03] text-center leading-none pointer-events-none select-none" style={{transform: 'translateY(40px)'}}>
          MCG
        </div>
        
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20 py-12 md:py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12 lg:gap-16">
            <div>
              <img 
                src="https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/7cc47221362597664001b9fe6feac620.png" 
                alt="Miami Comfort Group" 
                className="h-10 md:h-12 w-auto object-contain brightness-0 invert"
              />
              <h3 className="font-['Oxygen'] font-bold text-[24px] md:text-[28px] lg:text-[32px] text-white leading-[1.2] mb-6 md:mb-8">
                Strategic Real Estate<br />Consulting
              </h3>
              <p className="text-white/70 text-[13px] md:text-[14px] leading-[1.6]">
                Investing well starts with deciding well
              </p>
            </div>
            
            <div>
              <h4 className="font-['Oxygen'] font-bold text-[14px] md:text-[16px] text-white uppercase tracking-wider mb-4 md:mb-6">Services</h4>
              <div className="space-y-3 md:space-y-4">
                {[
                  'Strategic Consulting',
                  'Company & Legal Structure',
                  'Financing & Loans',
                  'Market Studies',
                  'Property Search',
                  'Property Management'
                ].map((link, index) => (
                  <div key={index}>
                    <Link to="/services" className="text-white/80 text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors cursor-pointer block">
                      {link}
                    </Link>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-['Oxygen'] font-bold text-[14px] md:text-[16px] text-white uppercase tracking-wider mb-4 md:mb-6">Company</h4>
              <div className="space-y-3 md:space-y-4">
                <Link to="/about" className="text-white/80 text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors block">
                  About Us
                </Link>
                <Link to="/#technology" className="text-white/80 text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors cursor-pointer block">
                  Technology
                </Link>
                <Link to="/#why-mcg" className="text-white/80 text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors cursor-pointer block">
                  Why MCG
                </Link>
                <Link to="/#contact" className="text-white/80 text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors cursor-pointer block">
                  Contact
                </Link>
                
                <div className="flex items-center gap-4 md:gap-5 mt-8 md:mt-10">
                  <a href="https://www.instagram.com/elisabet_comfyhouses/" target="_blank" rel="noopener noreferrer" className="w-6 h-6 flex items-center justify-center cursor-pointer">
                    <i className="ri-instagram-line text-[22px] md:text-[24px] text-white/60 hover:text-[#B4E4E0] transition-colors"></i>
                  </a>
                  <a href="https://www.linkedin.com/in/elisabet-sierra/" target="_blank" rel="noopener noreferrer" className="w-6 h-6 flex items-center justify-center cursor-pointer">
                    <i className="ri-linkedin-line text-[22px] md:text-[24px] text-white/60 hover:text-[#B4E4E0] transition-colors"></i>
                  </a>
                  <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="w-6 h-6 flex items-center justify-center cursor-pointer">
                    <i className="ri-youtube-line text-[22px] md:text-[24px] text-white/60 hover:text-[#B4E4E0] transition-colors"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-12 md:mt-16 lg:mt-20 pt-6 md:pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-[13px] md:text-[14px] text-center md:text-left">
              © 2026 Miami Comfort Group. All rights reserved.
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white/50 text-[13px] md:text-[14px] hover:text-[#B4E4E0] transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>

          <div className="mt-8 pt-6 border-t border-white/10 text-center space-y-3">
            <p className="text-[11px] font-semibold text-[#cccccc]">
              Elisabet Sierra Bermejo · Licensed Real Estate Sales Associate<br />
              Avanti Way Realty, LLC · License #SL3535387
            </p>
            <p className="text-[10px] text-[#888888] max-w-[800px] mx-auto leading-relaxed">
              Miami Comfort Group LLC is a brand name used for marketing purposes only. 
              Real estate services are provided by Elisabet Sierra Bermejo, licensed 
              with Avanti Way Realty, LLC.
            </p>
            <p className="text-[10px] text-[#888888]">
              © 2026 Miami Comfort Group · miamicomfort.group · hello@miamicomfort.group
            </p>
            <p className="text-[9px] text-[#888888] max-w-[700px] mx-auto leading-relaxed pt-2">
              Equal Housing Opportunity. We are pledged to the letter and spirit of U.S. policy for equal housing opportunity throughout the nation.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}